import type { RequestRatingDetailResponse } from "@nlc/shared-types";

import { AnimatePresence, motion } from "framer-motion";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { ToggleButton, useFilter } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Avatar } from "@/components/avatar";
import { Button } from "@/components/button";
import {
  CHIP_BASE,
  CHIP_NEUTRAL,
  CHIP_SELECTED,
} from "@/components/chip-styles";
import { SearchField, SearchInput } from "@/components/search-field";
import { Text } from "@/components/text";
import { useGetAssignableDesigners } from "@/features/request/edit/hooks/useGetAssignableDesigners";

import { SWAP_TRANSITION } from "./ratingMotion";

type Candidate = { avatar?: string; id: number; name: string };

type Participant = RequestRatingDetailResponse["participants"][number];

const BROWSE_LIMIT = 8;

interface RatingAssigneePickerProps {
  onChange: (designerId: null | number) => void;
  participants: Participant[];
  requestId: number;
  selectedAvatar?: string;
  selectedName: null | string;
  value: null | number;
}

function AssigneeChip({
  candidate,
  isSelected,
  onSelect,
}: {
  candidate: Candidate;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <ToggleButton
      aria-label={candidate.name}
      className={twMerge(
        CHIP_BASE,
        "flex items-center gap-xs py-xs pl-xs pr-lg",
        isSelected ? CHIP_SELECTED : CHIP_NEUTRAL,
      )}
      isSelected={isSelected}
      onChange={onSelect}
    >
      <Avatar
        alt={candidate.name}
        className="size-5 rounded-full"
        src={candidate.avatar}
      />
      {candidate.name}
    </ToggleButton>
  );
}

function dedupeById(candidates: Candidate[]) {
  const seen = new Set<number>();

  return candidates.filter(
    (candidate) => !seen.has(candidate.id) && seen.add(candidate.id),
  );
}

export function RatingAssigneePicker({
  onChange,
  participants,
  requestId,
  selectedAvatar,
  selectedName,
  value,
}: RatingAssigneePickerProps) {
  const { data: designers } = useGetAssignableDesigners(requestId);
  const { contains } = useFilter({ sensitivity: "base" });
  const [isSearching, setIsSearching] = useState(false);
  const [query, setQuery] = useState("");

  const fromParticipants: Candidate[] = participants.map((participant) => ({
    avatar: participant.avatar,
    id: participant.id,
    name: participant.name ?? `User ${participant.id}`,
  }));

  const fromDesigners: Candidate[] = (designers ?? []).map((designer) => ({
    avatar: designer.profilePicture,
    id: designer.id,
    name: designer.textValue,
  }));

  const selected =
    value == null
      ? null
      : ([...fromParticipants, ...fromDesigners].find(
          (candidate) => candidate.id === value,
        ) ?? {
          avatar: selectedAvatar,
          id: value,
          name: selectedName ?? `User ${value}`,
        });

  const suggested = dedupeById([
    ...(selected ? [selected] : []),
    ...fromParticipants,
  ]);

  const suggestedIds = new Set(suggested.map((candidate) => candidate.id));
  const others = fromDesigners.filter(
    (candidate) => !suggestedIds.has(candidate.id),
  );
  const results = query
    ? others.filter((candidate) => contains(candidate.name, query))
    : others.slice(0, BROWSE_LIMIT);

  return (
    <div className="flex flex-col gap-sm">
      <p className="text-md font-semibold text-text-primary">
        Assign this rating to
      </p>

      <div className="flex flex-wrap gap-sm">
        {suggested.map((candidate) => (
          <AssigneeChip
            candidate={candidate}
            isSelected={candidate.id === value}
            key={candidate.id}
            onSelect={() => onChange(candidate.id)}
          />
        ))}

        {!isSearching && (
          <Button
            className="flex items-center gap-xs rounded-full border border-dashed border-border-primary px-lg py-xs text-sm font-medium text-text-tertiary outline-hidden hover:bg-bg-secondary hover:text-text-secondary focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-border-brand-solid"
            onPress={() => setIsSearching(true)}
            variant="unstyled"
          >
            <PlusIcon className="size-4" />
            Someone else
          </Button>
        )}
      </div>

      <AnimatePresence>
        {isSearching && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            style={{ overflow: "hidden" }}
            transition={SWAP_TRANSITION}
          >
            <div className="flex flex-col gap-sm pt-xs">
              <SearchField aria-label="Search all designers" className="w-full">
                <SearchInput
                  autoFocus
                  onChange={(event) => setQuery(event.target.value)}
                  onClear={() => setQuery("")}
                  placeholder="Search all designers..."
                  value={query}
                />
              </SearchField>

              {results.length === 0 ? (
                <Text className="p-sm text-center text-text-tertiary">
                  No designers found
                </Text>
              ) : (
                <div className="flex flex-wrap gap-sm">
                  {results.map((candidate) => (
                    <AssigneeChip
                      candidate={candidate}
                      isSelected={candidate.id === value}
                      key={candidate.id}
                      onSelect={() => onChange(candidate.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
